import React, { useState } from "react";
import DOMPurify from "dompurify";

import styles from "./aiSettings.module.scss";
import { isOverviewTranslationRun, isTranslationTask } from "./taskKinds";

// The Overview's four fields, in the order the app shows them. Notable Quotes
// is no longer generated (the backend's output format dropped it).
const OVERVIEW_FIELDS = [
  ["aboutTheBook", "About the Book"],
  ["shouldReadFor", "Who Should Read It"],
  ["actionableInsights", "Actionable Insights"],
  ["reflectionQuestions", "Questions Worth Asking"],
];

// The review's verdict, as the badge says it.
const REVIEW_WORDS = {
  approved: "no changes",
  corrected: "corrected",
  needs_editorial_review: "needs editor",
  failed: "review failed",
};

/** What the badge counts, per task. */
const badgeOf = (run) => {
  if (run.key === "overviewGeneration") return `${run.parsed}/${run.expected ?? OVERVIEW_FIELDS.length}`;
  if (run.review) return `${REVIEW_WORDS[run.review.status] || run.review.status} · ${run.language?.toUpperCase() || "—"}`;
  if (isOverviewTranslationRun(run)) return `${run.parsed}/${run.expected} · ${run.language?.toUpperCase() || "—"}`;
  if (isTranslationTask(run.key)) return run.language?.toUpperCase() || "—";
  return `${run.parsed} ch`;
};

const titleOf = (run) => {
  if (isOverviewTranslationRun(run)) return run.bookTitle;
  if (isTranslationTask(run.key)) return "Sample translation";
  return run.bookTitle;
};

/**
 * One test run: what it cost, and what it actually produced.
 *
 * Collapsed to a header line, because ten runs are more than a page can show
 * at once. Open one and you read the output as the app will render it — the
 * chapters, the four Overview sections, or the translation. `Raw output` is
 * there for the case the page exists to catch: a prompt whose reply the
 * parser could not use.
 */
const RunCard = ({ run, isOpen, onToggle, onReuse, onOpenInSummary }) => {
  const [showRaw, setShowRaw] = useState(false);
  const failed = !(run.parsed > 0);
  const seconds = run.ms ? (run.ms / 1000).toFixed(1) : null;
  const hasRaw = !isTranslationTask(run.key);
  // Only an Overview run that parsed, for a book that has a summary to put it on.
  const canOpenInSummary = onOpenInSummary && run.key === "overviewGeneration" && !failed && run.summaryId;

  return (
    <div className={styles.runCard}>
      <button type="button" className={styles.runHeader} onClick={onToggle}>
        <span className={failed ? styles.runBadgeFail : styles.runBadgePass}>{badgeOf(run)}</span>
        <span className={styles.runTitle}>{titleOf(run)}</span>
        <span className={styles.runMeta}>
          {run.provider === "openrouter" ? "OpenRouter · " : ""}
          {run.model}
          {seconds && ` · ${seconds}s`}
          {run.usage?.totalTokens ? ` · ${run.usage.totalTokens.toLocaleString()} tokens` : ""}
          {run.finishReason === "length" && " · hit the token ceiling"}
        </span>
        <span className={styles.runTime}>
          {run.at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </button>

      {isOpen && (
        <div className={styles.runBody}>
          <div className={styles.runActions}>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onReuse}>
              Use these settings
            </button>
            {canOpenInSummary && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={onOpenInSummary}
                title="Opens the summary in a new tab with this Overview filled in — nothing is saved until you press Update there"
              >
                Open in summary
              </button>
            )}
            {hasRaw && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowRaw(!showRaw)}>
                {showRaw ? "Show parsed" : "Raw output"}
              </button>
            )}
          </div>

          {showRaw ? (
            <pre className={styles.runRaw}>{run.raw || "(the model returned nothing)"}</pre>
          ) : run.key === "overviewGeneration" ? (
            OVERVIEW_FIELDS.map(([field, label]) => (
              <div key={field} className={styles.runChapter}>
                <h6>{label}</h6>
                {run.fields?.[field] ? (
                  <div className={styles.runChapterBody} dangerouslySetInnerHTML={{ __html: run.fields[field] }} />
                ) : (
                  <p className={styles.hint}>Missing from the reply.</p>
                )}
              </div>
            ))
          ) : isOverviewTranslationRun(run) ? (
            // Each Overview section: the English the summary holds, then the
            // translation this run produced — what Save would put on it. A
            // review run shows the first draft too, where the review changed it.
            <>
            {run.review?.problems?.length > 0 && (
              <p className={styles.hint}>Why the review was not used: {run.review.problems.join(" · ")}</p>
            )}
            {run.fields.map((section) => (
              <div key={section.field} className={styles.runChapter}>
                <h6>
                  {section.label}
                  {section.changed && " · corrected by the review"}
                </h6>
                <div className={styles.runChapterBody} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.source || "") }} />
                {section.changed && (
                  <div
                    className={styles.runChapterBody}
                    style={{ borderTop: "1px dashed var(--ps-border)", marginTop: 8, paddingTop: 8, opacity: 0.6 }}
                  >
                    <small>First draft</small>
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.draft || "") }} />
                  </div>
                )}
                {section.translated ? (
                  <div
                    className={styles.runChapterBody}
                    style={{ borderTop: "1px dashed var(--ps-border)", marginTop: 8, paddingTop: 8 }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.translated) }}
                  />
                ) : (
                  <p className={styles.hint}>Not translated in this run.</p>
                )}
              </div>
            ))}
            </>
          ) : isTranslationTask(run.key) ? (
            <>
              <div className={styles.runChapter}>
                <h6>English</h6>
                <div className={styles.runChapterBody} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(run.source || "") }} />
              </div>
              <div className={styles.runChapter}>
                <h6>Translated</h6>
                {/* The model's own text — sanitised, since a translation is not
                    escaped server-side the way parsed chapters are. */}
                <div
                  className={styles.runChapterBody}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(run.translated || "") }}
                />
              </div>
            </>
          ) : (
            run.chapters?.map((chapter, i) => (
              <div key={i} className={styles.runChapter}>
                <h6>{chapter.chapterName}</h6>
                <div className={styles.runChapterBody} dangerouslySetInnerHTML={{ __html: chapter.content || "" }} />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default RunCard;
